import {
  LIBERTY_ROLES,
  PROPERTY_ROLES,
  COMPANY_SPECIFIC_ROLES,
  getRoleDisplayName,
} from '@/constants/roles'
import type { RolesConfigCategory, RoleConfigLookup } from '../types'

export type { RoleConfigLookup }

function getRoleCategory(
  roleName: string,
): 'Accounts' | 'Companies' | 'Properties' | 'Other' {
  if ((LIBERTY_ROLES as readonly string[]).includes(roleName)) return 'Accounts'
  if ((COMPANY_SPECIFIC_ROLES as readonly string[]).includes(roleName)) return 'Companies'
  if ((PROPERTY_ROLES as readonly string[]).includes(roleName)) return 'Properties'
  return 'Other'
}

/**
 * Build title/type maps from GET /roles (Angular filter_user_roles).
 */
export function buildRoleConfigLookup(
  config: Record<string, RolesConfigCategory>,
): RoleConfigLookup {
  const titleByRoleName: Record<string, string> = {}
  const typeByRoleName: Record<string, string> = {}

  Object.values(config).forEach((category) => {
    Object.entries(category.roles || {}).forEach(([roleName, roleDef]) => {
      titleByRoleName[roleName] = roleDef.name
      typeByRoleName[roleName] = category.name
    })
  })

  return { titleByRoleName, typeByRoleName }
}

/** Used when /roles has not loaded yet or a role key is missing from the API. */
export function getFallbackRoleType(roleName: string): string {
  const category = getRoleCategory(roleName)
  if (category === 'Other') return category
  return `Liberty ${category}`
}

/**
 * Angular user-role-selection: replace "Liberty" in category heading with org name.
 */
export function formatRoleTypeLabel(roleType: string, orgName: string): string {
  if (roleType.includes('Liberty') && orgName !== 'Liberty Rent Guarantee') {
    return roleType.replace('Liberty', orgName)
  }
  return roleType
}

/**
 * Sidebar / filter_user_roles parity: prefer GET /roles title, then static fallback.
 */
export function resolveRoleTitle(
  roleName: string | undefined | null,
  lookup: RoleConfigLookup,
): string {
  if (!roleName) return 'Unknown Role'
  return lookup.titleByRoleName[roleName] || getRoleDisplayName(roleName)
}
